import { useEffect } from "react";

/**
 * Since InvestorLayout's `hero` is position:absolute (not fixed), pages taller
 * than one viewport scroll at the document level. During that scroll the mouse
 * cursor stays put while row content moves underneath it, and browsers still
 * re-evaluate :hover for whatever ends up under a stationary cursor — rows
 * flash their hover tint mid-scroll even though the user never moved the mouse.
 *
 * Toggling `pointer-events: none` on <body> while scrolling (see the
 * `body.is-scrolling` rule in index.css) suppresses :hover — and therefore the
 * flash — without touching every hover-styled element individually. Restored
 * a short beat after scrolling stops so a deliberate click right after a swipe
 * still lands normally.
 */
const SCROLL_END_DELAY_MS = 150;

export function useSuppressHoverOnScroll(): void {
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      document.body.classList.add("is-scrolling");
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        document.body.classList.remove("is-scrolling");
      }, SCROLL_END_DELAY_MS);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
      document.body.classList.remove("is-scrolling");
    };
  }, []);
}
